import React, { useEffect, useState, useRef } from "react"
import Value from "aldoo-ra/Value"
import useAuth from "aldoo-ra/PlatformAuth/actions/auth"
import { PermissionAPI } from "aldoo-ra/Permissions"
import getRealtimeDeploymentController from "./realtime-deployment-controller"
import { showDeploymentUpdateToast, dismissDeploymentToast } from "./deployment-toast"
import PubSub from "pubsub-js"

// Create controller instance outside component
const controller = getRealtimeDeploymentController()

// Singleton state holders outside the component
let appListData = null
let statusesData = null
let updateListeners = new Set()
let initialDataReceived = false
let notifiedUpdates = new Map() // Track what updates have been notified: appId -> { revision, updateCount }
let deploymentToolConnected = false // Track deployment tool connection status
let deploymentToolConnectionChecked = false // Track if we've received initial connection status

const notifyListeners = () => {
  updateListeners.forEach((listener) => listener())
}

export function useAppUpdates() {
  const [refreshKey, setRefreshKey] = useState(0)
  const [initialDataLoaded, setInitialDataLoaded] = useState(initialDataReceived)
  const [toolConnectionChecked, setToolConnectionChecked] = useState(deploymentToolConnectionChecked)
  const [toolConnected, setToolConnected] = useState(deploymentToolConnected)

  useEffect(() => {
    const refresh = () => {
      setRefreshKey((prev) => prev + 1)
      setInitialDataLoaded(initialDataReceived)
      setToolConnectionChecked(deploymentToolConnectionChecked)
      setToolConnected(deploymentToolConnected)
    }
    updateListeners.add(refresh)
    return () => updateListeners.delete(refresh)
  }, [])

  return {
    appList: appListData,
    statuses: statusesData,
    refreshKey,
    initialDataLoaded,
    toolConnectionChecked,
    toolConnected,
  }
}

function AdminSiteUpdateScan() {
  const [, setUpdatesCount] = Value("site-updates")
  const [auth] = useAuth()
  const [isAdmin, setIsAdmin] = useState(false)
  const [isAdminPath, setIsAdminPath] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [connectionError, setConnectionError] = useState(null)
  const [initialDataLoaded, setInitialDataLoaded] = useState(false)

  // Check if we're on admin path
  useEffect(() => {
    const currentPath = window.location.pathname
    const isAdmin = currentPath.startsWith("/admin")
    setIsAdminPath(isAdmin)
  }, [])

  // Check if user has admin permissions
  useEffect(() => {
    const checkAdminPermission = async () => {
      if (!auth || !auth._id) {
        setIsAdmin(false)
        return
      }

      try {
        const hasAdminPermission = await PermissionAPI.hasPermission(auth._id, "role:admin")
        setIsAdmin(hasAdminPermission)
      } catch (error) {
        console.error("Failed to check admin permission:", error)
        setIsAdmin(false)
      }
    }

    checkAdminPermission()
  }, [auth])

  // Method to update the total updates count
  const updateTotalUpdatesCount = async (statuses) => {
    if (!statuses) {
      setUpdatesCount(0)
      return
    }

    // Use cached deployment tool connection status
    // If deployment tool is not connected, hide the badge
    if (!deploymentToolConnected) {
      setUpdatesCount(0)
      return
    }

    const totalUpdates = Array.from(statuses.values()).reduce(
      (total, status) => {
        // Prioritize the new realtime format
        if (status?.gitState?.updates && Array.isArray(status.gitState.updates)) {
          return total + status.gitState.updates.length
        }
        // Fallback to old format for backward compatibility
        if (status && Array.isArray(status.updates)) {
          return total + status.updates.length
        }
        return total
      },
      0
    )

    // Always call setUpdatesCount, even if totalUpdates is 0
    setUpdatesCount(totalUpdates)
  }

  // Listen for deployment tool connection status changes
  useEffect(() => {
    if (!isAdminPath || !isAdmin) return

    const handleConnectionChange = async (msg, data) => {
      // Update cached connection status
      deploymentToolConnected = data?.connected || false
      // Update badge based on new connection status
      await updateTotalUpdatesCount(statusesData)
    }

    // Subscribe to deployment tool connection changes
    const token = PubSub.subscribe('DEPLOYMENT_TOOL_CONNECTION_CHANGED', handleConnectionChange)

    return () => {
      PubSub.unsubscribe(token)
    }
  }, [isAdminPath, isAdmin])

  // Add timeout to auto-retry if no initial data received
  useEffect(() => {
    if (!isAdminPath || !isAdmin || !isInitialized) return
    if (initialDataReceived) return // Already have data

    // Set timeout to retry subscription if no data received
    // Increased timeout and only trigger if controller is not connected
    const timeoutId = setTimeout(() => {
      if (!initialDataReceived && !controller.isConnected) {
        console.warn('⏱️ No initial data after 30s and not connected, forcing reconnection...')

        // Force reconnection only if actually disconnected
        controller.cleanup()
        setIsInitialized(false)
        // Re-initialization will happen automatically via the main effect
      }
    }, 30000) // 30 second timeout (increased from 5 seconds)

    return () => clearTimeout(timeoutId)
  }, [isAdminPath, isAdmin, isInitialized, initialDataReceived])

  // Initialize realtime connection and fetch initial data
  useEffect(() => {
    if (!isAdminPath || !isAdmin || isInitialized) return

    const initializeRealtimeConnection = async () => {
      try {
        // Initialize the controller - rt() handles auth automatically
        controller.initialize()

        // Setup event listeners for real-time updates
        controller.setupEventListeners({
          onToolConnectionStatus: (status) => {
            // Immediately update deployment tool connection status on connect/disconnect
            if (status.success && status.connection) {
              deploymentToolConnected = !!status.connection.connected
              deploymentToolConnectionChecked = true // Mark that we've received the status

              // Always publish the connection status (for both connect and disconnect)
              PubSub.publish('DEPLOYMENT_TOOL_CONNECTION_CHANGED', { connected: deploymentToolConnected })

              // Update badge immediately based on connection status
              updateTotalUpdatesCount(statusesData)

              // Notify listeners to refresh UI
              notifyListeners()
            }
          },

          onAppUpdates: (updates) => {
            if (updates.data) {
              // Only update appListData if this is a full dataset, not a partial update
              if (!updates.partial) {
                appListData = updates.data
              }

              // Convert app data to statuses format for compatibility
              const statusMap = new Map(statusesData) // Start with existing data

              updates.data.forEach(app => {
                // Get existing app data to preserve core info
                const existingApp = statusMap.get(app.id) || {}

                // Extract status from deployment object or gitState
                const deploymentStatus = app.deployment?.status || app.gitState?.status
                const appStatus = app.status || deploymentStatus || "updated" // Default to "updated" instead of "unknown"

                // Merge new data with existing, preserving core app info
                statusMap.set(app.id, {
                  // Preserve existing core app info, use new data as fallback
                  id: app.id,
                  name: existingApp.name || app.name || "Unknown App",
                  type: existingApp.type || app.type || "web",
                  description: existingApp.description || app.description || "",

                  // Update git and deployment states
                  gitState: app.gitState || {
                    status: appStatus,
                    branch: app.deployment?.BRANCH || app.branch || existingApp.gitState?.branch || "main",
                    destination: app.destination || existingApp.gitState?.destination,
                    localRevision: app.localRevision || app.deployment?.localRevision || existingApp.gitState?.localRevision,
                    updates: app.updates || existingApp.gitState?.updates || []
                  },
                  deploymentState: app.deploymentState != null
                    ? { status: "idle", taskId: null, progress: null, currentStep: null, completedAt: null, elapsed: 0, ...app.deploymentState }
                    : (existingApp.deploymentState || { status: "idle", taskId: null, progress: null, currentStep: null, completedAt: null, elapsed: 0 }),

                  updateTask: app.updateTask || existingApp.updateTask
                })
              })

              // Only replace statusesData if this is a full update or if it's empty
              if (!updates.partial || statusesData.size === 0) {
                statusesData = statusMap
              } else {
                // For partial updates, only update the apps that were included
                updates.data.forEach(app => {
                  if (statusMap.has(app.id)) {
                    statusesData.set(app.id, statusMap.get(app.id))
                  }
                })
              }

              // Update deployment activity tracking for auto-reconnection
              controller.updateDeploymentActivity(statusesData)

              // Notify listeners to update UI with app list
              notifyListeners()

              // Mark as initial data received when we have the apps loaded
              // This happens when appListData is populated with actual apps
              if (!initialDataReceived && appListData && appListData.length > 0) {
                  initialDataReceived = true
                setInitialDataLoaded(true)
              }

              // Then update badge count and show toasts after UI is updated
              updateTotalUpdatesCount(statusesData)

              // Show deployment toasts for apps with pending updates
              // Only show toasts if we're not on the deployment page itself and user is admin
              const currentPath = window.location.pathname
              const isDeploymentPage = currentPath.includes('/admin/updates')


              if (!isDeploymentPage && isAdmin) {
                updates.data.forEach(app => {
                  const hasUpdates = app.gitState?.updates?.length > 0 ||
                                   (app.gitState?.status === 'pending')
                  const isNotDeploying = app.deploymentState?.status !== 'running'
                  const updateCount = app.gitState?.updates?.length || 0
                  const currentRevision = app.gitState?.localRevision || app.localRevision || 'unknown'

                  // Check if we've already notified about this exact state
                  const notificationKey = `${app.id}`
                  const lastNotified = notifiedUpdates.get(notificationKey)
                  const hasNewUpdates = !lastNotified ||
                                       lastNotified.revision !== currentRevision ||
                                       lastNotified.updateCount !== updateCount


                  if (hasUpdates && isNotDeploying && hasNewUpdates) {
                    // Track that we've notified about this state
                    notifiedUpdates.set(notificationKey, {
                      revision: currentRevision,
                      updateCount: updateCount,
                      timestamp: Date.now()
                    })
                    // Show toast for this app with current revision data
                    showDeploymentUpdateToast({
                      ...app,
                      currentRevision
                    })
                  // Removed auto-dismissal - only user can dismiss toasts manually
                  }
                })
              }
            }
          },

          onDeploymentComplete: (completion) => {
            notifyListeners()
          },

          onDeploymentFailed: (failure) => {
            notifyListeners()
          },

          onTaskUpdate: (taskStatus) => {
            if (taskStatus && statusesData) {
              let targetAppId = null
              let appData = null

              // If broadcast includes appId, use it directly (deployment progress updates)
              if (taskStatus.appId && statusesData.has(taskStatus.appId)) {
                targetAppId = taskStatus.appId
                appData = statusesData.get(targetAppId)
              }
              // Otherwise, try to match by task ID (legacy flow)
              else if (taskStatus.taskId) {
                for (const [appId, data] of statusesData.entries()) {
                  const directMatch = data.updateTask === taskStatus.taskId
                  const objectIdMatch = data.updateTask && data.updateTask.id === taskStatus.taskId

                  if (data.updateTask && (directMatch || objectIdMatch)) {
                    targetAppId = appId
                    appData = data
                    break
                  }
                }
              }

              // If we found a matching app, update it
              if (targetAppId && appData) {
                // Update the app's deployment state with progress
                if (!appData.deploymentState) {
                  appData.deploymentState = {}
                }

                // Update deployment progress and status
                if (taskStatus.progress !== undefined) {
                  appData.deploymentState.progress = taskStatus.progress
                }

                if (taskStatus.status) {
                  const isFailed = taskStatus.status === 'error' || taskStatus.status === 'failed'
                  const newStatus = taskStatus.status === 'completed' ? 'completed' : isFailed ? 'failed' : 'running'
                  appData.deploymentState.status = newStatus
                  if (isFailed && taskStatus.error) {
                    appData.deploymentState.error = taskStatus.error
                  }
                }

                if (taskStatus.currentStep || taskStatus.activity) {
                  appData.deploymentState.currentStep = taskStatus.currentStep || taskStatus.activity
                }

                // Deployment start time from the deploy tool — single source of truth for "Elapsed"
                if (taskStatus.startedAt) {
                  appData.deploymentState.startedAt = taskStatus.startedAt
                }
                if (typeof taskStatus.elapsed === 'number') {
                  appData.deploymentState.elapsed = taskStatus.elapsed
                }

                if (taskStatus.status === 'completed') {
                  appData.deploymentState.completedAt = new Date().toISOString()

                  // After a short delay, reset deployment state to allow UI to return to normal
                  setTimeout(() => {
                    if (appData.deploymentState && appData.deploymentState.status === 'completed') {
                      // Update git state to reflect that deployment applied the updates
                      if (appData.gitState && appData.gitState.updates) {
                        appData.gitState.status = 'updated'
                        appData.gitState.updates = [] // Clear pending updates since they were deployed
                      }

                      // Clear the deployment state completely
                      appData.deploymentState = {
                        status: 'idle',
                        taskId: null,
                        progress: null,
                        currentStep: null,
                        completedAt: null
                      }
                      appData.updateTask = null // Clear the update task

                      // Single notification to update the UI
                      notifyListeners()
                      updateTotalUpdatesCount(statusesData)

                      // Dispatch custom event for toast components
                      document.dispatchEvent(new CustomEvent('deploymentAppUpdate', {
                        detail: appData
                      }))
                    }
                  }, 1000) // 1 second delay to show "completed" state - reduced from 3 seconds to prevent UI loading state timeout
                }

                // Dispatch custom event for toast components
                document.dispatchEvent(new CustomEvent('deploymentTaskUpdate', {
                  detail: taskStatus
                }))

                // Also dispatch app update event
                document.dispatchEvent(new CustomEvent('deploymentAppUpdate', {
                  detail: appData
                }))
              }
            }

            // Update deployment activity tracking after task updates
            controller.updateDeploymentActivity(statusesData)

            notifyListeners()
          }
        })

        // Subscribe to the appropriate environment
        const environment = window.location.hostname.includes('stage') ||
                           window.location.hostname.includes('localhost')
                           ? 'staging'
                           : 'production'

        // Only subscribe to environment - no more HTTP-style getAppList calls
        await controller.subscribeToEnvironment(environment)

        // Initialize updates count to 0 until we receive data
        setUpdatesCount(0)

        setIsInitialized(true)
        setConnectionError(null)

      } catch (error) {
        console.error('AdminSiteUpdateScan: Failed to initialize realtime connection:', error)
        setConnectionError(error.message)
      }
    }

    initializeRealtimeConnection()

    // Cleanup on unmount
    return () => {
      if (isInitialized) {
        controller.cleanup()
        setIsInitialized(false)
      }
    }
  }, [isAdminPath, isAdmin, isInitialized])


  return null // This component doesn't render anything
}

export default AdminSiteUpdateScan